import {
    Component,
    h,
    Prop,
    ComponentInterface,
    Host,
    Event,
    EventEmitter,
    Watch,
} from "@stencil/core";

export interface ListItemValueChange {
    previous: string;
    current: string;
}

/**
 * @slot - Default content, intended for single line of text.
 * @slot primary - Content that should appear below default content, intended for visually pronounced text.
 * @slot icon - Custom icon in front of both content slots.
 */
@Component({
    tag: "uic-list-item",
    styleUrl: "list-item.scss",
})
export class ListItem implements ComponentInterface {
    /**
     * If set to `true`, the list item is considered selected.
     */
    @Prop({ reflect: true }) selected = false;

    /**
     * If set to `true`, the user cannot interact with this element.
     */
    @Prop({ reflect: true }) disabled = false;

    /**
     * The value.
     */
    @Prop() value: string;

    /**
     * If set to `true`, enables an additional slot for secondary content.
     */
    @Prop({ reflect: true }) multiline = false;

    /**
     * The name of the icon to show next to the label.
     */
    @Prop()
    icon?: string;

    /**
     * @internal Event to request selection.
     */
    @Event({ eventName: "uicSelect" })
    select: EventEmitter;

    /**
     * Notifies containing list about item being added.
     *
     * @internal
     */
    @Event({ eventName: "uicItemAdded" })
    added: EventEmitter<string>;

    /**
     * Notifies containing list about item being updated.
     *
     * @internal
     */
    @Event({ eventName: "uicItemUpdated" })
    updated: EventEmitter<ListItemValueChange>;

    /**
     * This flag is essential to control uicItemAdded emitted only after item
     * was rendered properly at least once.
     */
    private notifiedOnAddedBefore = false;

    private onClick() {
        this.select.emit();
    }

    public connectedCallback() {
        if (this.notifiedOnAddedBefore) {
            this.added.emit(this.value);
        }
    }

    public componentDidLoad() {
        this.added.emit(this.value);
        this.notifiedOnAddedBefore = true;
    }

    @Watch("value")
    public onValueChanged(current: string, previous: string): void {
        if (this.notifiedOnAddedBefore) {
            this.updated.emit({ previous, current });
        }
    }

    render() {
        return (
            <Host
                uic-item
                onClick={() => this.onClick()}
                tabindex={this.selected && !this.disabled ? 0 : -1}
            >
                <div class="uic-list-item__wrapper">
                    <slot name="icon">
                        {this.icon && (
                            <uic-icon
                                class="uic-list-item__icon"
                                name={this.icon}
                            ></uic-icon>
                        )}
                    </slot>
                    <div class="uic-list-item__content">
                        <div class="uic-list-item__content-default">
                            <slot></slot>
                        </div>
                        <div class="uic-list-item__content-default uic-list-item__content-primary">
                            <slot name="primary"></slot>
                        </div>
                    </div>
                </div>
            </Host>
        );
    }
}
